import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Alert,
} from '@mui/material'
import { t } from '@lingui/macro'
import type { MaternityAttendanceRow } from '@/core/api/statistics'
import { MATERNITY_AGE_BUCKETS, pivotMaternityAttendance } from '../../lib/monthlyReportHelpers'

type Props = {
  data?: MaternityAttendanceRow[]
  isLoading: boolean
  error?: unknown
  multiEstablishment: boolean
}

export function MaternityAttendanceTable({ data, isLoading, error, multiEstablishment }: Props) {
  if (isLoading) return <CircularProgress size={24} />
  if (error) return <Alert severity="error">{t`Error loading maternity data`}</Alert>
  if (!data || data.length === 0) return <Typography color="text.secondary">{t`No data available`}</Typography>

  const rows = pivotMaternityAttendance(data)
  const ageTotals: Record<string, number> = {}
  let grandTotal = 0
  for (const row of rows) {
    for (const bucket of MATERNITY_AGE_BUCKETS) {
      ageTotals[bucket] = (ageTotals[bucket] || 0) + (row.ageCounts[bucket] || 0)
    }
    grandTotal += row.total
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            {multiEstablishment && (
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t`Location`}</TableCell>
            )}
            {MATERNITY_AGE_BUCKETS.map(bucket => (
              <TableCell key={bucket} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                {bucket}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t`Total`}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.establishmentName} hover>
              {multiEstablishment && (
                <TableCell sx={{ fontWeight: 500 }}>{row.establishmentName}</TableCell>
              )}
              {MATERNITY_AGE_BUCKETS.map(bucket => (
                <TableCell key={bucket} align="center">{row.ageCounts[bucket] || 0}</TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{row.total}</TableCell>
            </TableRow>
          ))}
          {multiEstablishment && (
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>{t`Total`}</TableCell>
              {MATERNITY_AGE_BUCKETS.map(bucket => (
                <TableCell key={bucket} align="center" sx={{ fontWeight: 'bold' }}>{ageTotals[bucket] || 0}</TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{grandTotal}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
